import { useEffect, useState } from "react";
import axios from "axios";
import { Settings as SettingsIcon, Loader2, Save, Home, Database, ChevronRight, Grid3x3, Pin, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crawlMethod, setCrawlMethod] = useState("beautifulsoup");
  const [columnCount, setColumnCount] = useState(4);
  const [stickyTabBar, setStickyTabBar] = useState(true);
  const [imageHoverPreview, setImageHoverPreview] = useState(true);
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [crawlResponse, columnResponse, stickyResponse, hoverPreviewResponse] = await Promise.all([
        axios.get(`${API}/settings/crawl_method`),
        axios.get(`${API}/settings/column_count`),
        axios.get(`${API}/settings/sticky_tab_bar`),
        axios.get(`${API}/settings/image_hover_preview`)
      ]);
      
      if (crawlResponse.data.value) {
        setCrawlMethod(crawlResponse.data.value);
      }
      
      if (columnResponse.data.value) {
        setColumnCount(parseInt(columnResponse.data.value));
      }
      
      if (stickyResponse.data.value !== undefined) {
        setStickyTabBar(stickyResponse.data.value === "true");
      }
      
      if (hoverPreviewResponse.data.value !== undefined) {
        setImageHoverPreview(hoverPreviewResponse.data.value === "true");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.post(`${API}/settings`, {
          setting_key: "crawl_method",
          setting_value: crawlMethod,
        }),
        axios.post(`${API}/settings`, {
          setting_key: "column_count",
          setting_value: columnCount.toString(),
        }),
        axios.post(`${API}/settings`, {
          setting_key: "sticky_tab_bar",
          setting_value: stickyTabBar.toString(),
        }),
        axios.post(`${API}/settings`, {
          setting_key: "image_hover_preview",
          setting_value: imageHoverPreview.toString(),
        })
      ]);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SettingsIcon className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Settings
              </h1>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Home className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cache Settings Card - Quick Access */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/cache-settings")}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Database className="w-6 h-6 text-purple-600" />
                    <div>
                      <CardTitle>Cache Settings</CardTitle>
                      <CardDescription>
                        Configure HTML and image caching behavior
                      </CardDescription>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    <strong>Quick settings:</strong> Enable/disable caching, set TTL duration, and clear cache to improve performance
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Crawl Method Card */}
            <Card>
              <CardHeader>
                <CardTitle>Crawl Method</CardTitle>
                <CardDescription>
                  Choose how the application fetches data from websites
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="crawl-method">Crawling Engine</Label>
                  <Select value={crawlMethod} onValueChange={setCrawlMethod}>
                    <SelectTrigger id="crawl-method">
                      <SelectValue placeholder="Select crawl method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beautifulsoup">
                        BeautifulSoup (Default)
                      </SelectItem>
                      <SelectItem value="curl">
                        cURL (Alternative)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {crawlMethod === "beautifulsoup"
                      ? "Uses Python requests + BeautifulSoup for web scraping. Reliable and feature-rich."
                      : "Uses cURL for fetching pages. Faster and lighter alternative."}
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    About Crawl Methods:
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>• <strong>BeautifulSoup:</strong> More robust HTML parsing, handles complex pages better</li>
                    <li>• <strong>cURL:</strong> Faster performance, lower resource usage, good for simple pages</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Grid Layout Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Grid3x3 className="w-6 h-6 text-green-600" />
                  <div>
                    <CardTitle>Grid Layout</CardTitle>
                    <CardDescription>
                      Customize the number of columns in article grid
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="column-count">Number of Columns</Label>
                  <Select 
                    value={columnCount.toString()} 
                    onValueChange={(val) => setColumnCount(parseInt(val))}
                  >
                    <SelectTrigger id="column-count">
                      <SelectValue placeholder="Select column count" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 Columns (Large cards)</SelectItem>
                      <SelectItem value="3">3 Columns</SelectItem>
                      <SelectItem value="4">4 Columns (Default)</SelectItem>
                      <SelectItem value="5">5 Columns</SelectItem>
                      <SelectItem value="6">6 Columns (Compact)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Choose between 2-6 columns. More columns = more articles visible but smaller cards.
                  </p>
                </div>

                {/* Preview */}
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3">
                    Layout Preview:
                  </h4>
                  <div className={`grid gap-2 grid-cols-${columnCount}`}>
                    {[...Array(columnCount)].map((_, i) => (
                      <div 
                        key={i} 
                        className="aspect-video bg-gradient-to-br from-green-200 to-green-300 dark:from-green-700 dark:to-green-600 rounded border border-green-300 dark:border-green-600"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                    Current: {columnCount} columns per row (on desktop)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Sticky Tab Bar Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Pin className="w-6 h-6 text-orange-600" />
                  <div>
                    <CardTitle>Sticky Tab Bar</CardTitle>
                    <CardDescription>
                      Pin the tab bar at the top when scrolling
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="sticky-tab">Keep tab bar fixed</Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tab bar will remain visible at the top when scrolling
                    </p>
                  </div>
                  <Switch
                    id="sticky-tab"
                    checked={stickyTabBar}
                    onCheckedChange={setStickyTabBar}
                  />
                </div>

                {/* Info Box */}
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">
                    {stickyTabBar ? "✅ Sticky Enabled" : "❌ Sticky Disabled"}
                  </h4>
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    {stickyTabBar
                      ? "Tab bar will stay fixed at the top, making it easy to switch between sources while scrolling through articles."
                      : "Tab bar will scroll with the page content."}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Image Hover Preview Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Eye className="w-6 h-6 text-indigo-600" />
                  <div>
                    <CardTitle>Image Hover Preview</CardTitle>
                    <CardDescription>
                      Show fullscreen preview when hovering over images
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="hover-preview">Enable hover preview</Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Images will show in fullscreen when you hover over them
                    </p>
                  </div>
                  <Switch
                    id="hover-preview"
                    checked={imageHoverPreview}
                    onCheckedChange={setImageHoverPreview}
                  />
                </div>

                {/* Info Box */}
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-2">
                    {imageHoverPreview ? "✅ Preview Enabled" : "❌ Preview Disabled"}
                  </h4>
                  <p className="text-sm text-indigo-800 dark:text-indigo-200">
                    {imageHoverPreview
                      ? "Hover over any image to see it in fullscreen. The preview automatically adjusts to fit your viewport - landscape images fit to height, portrait images fit to width."
                      : "Hover preview is disabled. Click on images to view them."}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={saveSettings}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
